import useFilterConfig from "../../../../hooks/useFilterConfig";
import { useURLQueryString } from "../../../../hooks/useUrlQueryString";
import { useCallback } from "react";
import debounce from "../../../../utils/debounce";
import classNames from "classnames";
import { Chip } from "@material-ui/core";
import { makeKey } from "../../../../utils/helpers";
import { FilterButton } from "../filter-button/filter-button.component";
import { FilterChipsProps, IExpectedKey } from "./filter-chips.props";
import { useFilterChipsStyles } from "./filter-chips.styles";
import { ClearAll } from "./components/clear-all/clear-all.component";

export const FilterChips = (props: FilterChipsProps) => {
  const showClearAll = props.showClearAll ?? true;
  const strict = props.strict ?? true;

  const { chipConfig } = useFilterConfig(props.filterType);
  const { clearParams, getValues, query, replaceHistory, values } = useURLQueryString(
    props.controlledParams
  );
  const classes = useFilterChipsStyles();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCallback = useCallback(
    debounce((params) => !!props.callback && props.callback(params), 1000),
    []
  );

  const handleClearAll = () => {
    clearParams("all", !props.callback);
    !!props.callback && props.callback({});
  };

  const handleDelete = (key: string, value: string) => {
    if (query.has(key)) {
      clearParams(["limit", "page"]);
      const V = query.getAll(key).join(",").split(",");

      if (V.includes(value)) {
        query.delete(key);

        // Re-add pair siblings
        if (V.length !== 1) {
          query.set(key, V.filter((v) => v !== value).join(","));
        }

        if (!props.callback) {
          replaceHistory();
        }
      }
    }

    if (props.callback) {
      debouncedCallback(getValues());
    }
  };

  const renderChipLabel = (value: string, format: false | IExpectedKey | undefined) => {
    if (format && format.render) {
      return format.render(value, values);
    }

    return value;
  };

  let keys = Object.keys(values);

  if (!!chipConfig && strict) {
    keys = keys.filter((key: string) => chipConfig.find((e) => e.name === key));
  }

  // @ts-ignore
  const Chips = keys.reduce((acc, key, i1: number) => {
    // When chipConfig is provided: apply defined custom class(es), if any
    const chips = values[key].map((chip: any, i2: number) => {
      const chipSpecs = !!chipConfig && chipConfig.find((e) => e.name === key);
      // @ts-ignore
      const className = (chipSpecs?.class ?? "") as string;

      const chipClasses = classNames({
        [classes.chip]: true,
        [className]: className !== "",
      });

      return (
        <Chip
          className={chipClasses}
          key={makeKey(i1, i2)}
          label={renderChipLabel(chip, chipSpecs)}
          onDelete={() => {
            handleDelete(key, chip);
          }}
        />
      );
    });

    return [...acc, ...chips];
  }, []);

  return (
    <div className={classes.filtersWrapper}>
      <div className={classes.chips}>
        <div>{Chips}</div>
        {!!Chips.length && showClearAll && <ClearAll onClick={handleClearAll} />}
      </div>
      <div className={classes.buttons}>
        {props.children}
        <FilterButton
          callback={props.callback}
          controlledParams={props.controlledParams}
          filterType={props.filterType}
          optionLists={props.optionLists || {}}
        />
      </div>
    </div>
  );
};
